import React, { useEffect } from "react";
import MuiIconButton from "@mui/material/IconButton";
import MuiTooltip from "@mui/material/Tooltip";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CloseIcon from "@mui/icons-material/Close";
import i18next from "../../../i18n/i18n";
import { AnalysisPanel, AnalysisPanelTabs } from "../../../components/AnalysisPanel";
import { AllProductSuggestionsPanel } from "../../../components/AllProductSuggestionsPanel/AllProductSuggestionsPanel";
import { AppType } from "../../../api/models";
import type { AllProductAutoSelectRuntimeConfig } from "../../../components/QuestionInput";
import styles from "../Chat.module.css";

interface QAExpandOverlayProps {
    expandedQAIndex: number;
    answers: any[];
    isStreaming: boolean;
    selectedAnswer: number;
    onClose: () => void;
    onNavigate: (index: number) => void;
    renderUnifiedAnswer: (answer: any, questionDetails: any, index: number, isStreaming: boolean, isHistory: boolean) => React.JSX.Element;
    renderUserQuestion: (index: number) => React.JSX.Element | null;

    // Analysis / suggestions panel — same state as main view
    shouldShowRightSidePanel: boolean;
    showSuggestionsPanel: boolean;
    activeCitation: string | undefined;
    selectedAnalysisAnswer: any;
    activeAnalysisPanelTab: AnalysisPanelTabs | undefined;
    selectedCitation: number;
    isAdmin: boolean;
    selectedAppType: AppType;
    selectedAnalysisAppType: AppType | undefined;
    thoughtsLoadingIndex: number | null;
    onToggleTab: (tab: AnalysisPanelTabs, index: number) => void;
    setSelectedCitation: (index: number) => void;

    // Suggestions panel callbacks
    products: any[];
    productScenarioRegenerateContext: any;
    handleProductScenarioRegenerateSelectProduct: any;
    handleProductScenarioRegenerateContinueWithAll: any;
    handleProductScenarioRegenerateCancel: any;
    allProductAutoSelectConfig?: AllProductAutoSelectRuntimeConfig;
    allowContinueWithAll: boolean;
}

const QAExpandOverlay: React.FC<QAExpandOverlayProps> = ({
    expandedQAIndex,
    answers,
    isStreaming,
    selectedAnswer,
    onClose,
    onNavigate,
    renderUnifiedAnswer,
    renderUserQuestion,
    shouldShowRightSidePanel,
    showSuggestionsPanel,
    activeCitation,
    selectedAnalysisAnswer,
    activeAnalysisPanelTab,
    selectedCitation,
    isAdmin,
    selectedAppType,
    selectedAnalysisAppType,
    thoughtsLoadingIndex,
    onToggleTab,
    setSelectedCitation,
    products,
    productScenarioRegenerateContext,
    handleProductScenarioRegenerateSelectProduct,
    handleProductScenarioRegenerateContinueWithAll,
    handleProductScenarioRegenerateCancel,
    allProductAutoSelectConfig,
    allowContinueWithAll
}) => {
    const total = answers.length;
    const answer = answers[expandedQAIndex];

    useEffect(() => {
        // Lock body scroll so the browser scrollbar never appears and the header always stays visible
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, []);

    useEffect(() => {
        if (total === 0) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            } else if (e.key === "ArrowLeft" && !showSuggestionsPanel && total > 1) {
                onNavigate((expandedQAIndex - 1 + total) % total);
            } else if (e.key === "ArrowRight" && !showSuggestionsPanel && total > 1) {
                onNavigate((expandedQAIndex + 1) % total);
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [expandedQAIndex, total, onClose, onNavigate, showSuggestionsPanel]);

    if (!answer) return null;

    const isThisAnswerStreaming = isStreaming && selectedAnswer === expandedQAIndex;

    return (
        <div className={styles.qaExpandOverlay} data-qa-expand-answer>
            {/* Fixed header — no question text, nav+close on right */}
            <div className={styles.qaExpandOverlayHeader}>
                <div className={styles.qaExpandOverlayHeaderRight}>
                    <MuiTooltip
                        title={
                            showSuggestionsPanel
                                ? i18next.t("chat.qaSwitchDisabledSuggestions", { defaultValue: "Complete the product selection before switching" })
                                : i18next.t("chat.qaNavigatePrevious", { defaultValue: "Previous" })
                        }
                        placement="bottom"
                    >
                        <span>
                            <MuiIconButton
                                size="small"
                                onClick={() => onNavigate((expandedQAIndex - 1 + total) % total)}
                                disabled={total <= 1 || showSuggestionsPanel}
                                aria-label="previous question"
                            >
                                <KeyboardArrowLeftIcon fontSize="small" />
                            </MuiIconButton>
                        </span>
                    </MuiTooltip>
                    <span className={styles.qaExpandOverlayCounter}>
                        {expandedQAIndex + 1}&nbsp;/&nbsp;{total}
                    </span>
                    <MuiTooltip
                        title={
                            showSuggestionsPanel
                                ? i18next.t("chat.qaSwitchDisabledSuggestions", { defaultValue: "Complete the product selection before switching" })
                                : i18next.t("chat.qaNavigateNext", { defaultValue: "Next" })
                        }
                        placement="bottom"
                    >
                        <span>
                            <MuiIconButton
                                size="small"
                                onClick={() => onNavigate((expandedQAIndex + 1) % total)}
                                disabled={total <= 1 || showSuggestionsPanel}
                                aria-label="next question"
                            >
                                <KeyboardArrowRightIcon fontSize="small" />
                            </MuiIconButton>
                        </span>
                    </MuiTooltip>
                    <div className={styles.qaExpandOverlayHeaderDivider} />
                    <MuiTooltip title={i18next.t("chat.qaCloseExpanded", { defaultValue: "Close full view" })} placement="bottom">
                        <MuiIconButton size="small" onClick={onClose} aria-label="close expanded view">
                            <CloseIcon fontSize="small" />
                        </MuiIconButton>
                    </MuiTooltip>
                </div>
            </div>

            {/* Body: scrollable Q+A on the left, side panel on the right */}
            <div
                className={`${styles.qaExpandOverlayBody} ${shouldShowRightSidePanel ? styles.qaExpandOverlayBodyWithPanel : ""} ${showSuggestionsPanel ? styles.qaExpandOverlayBodySuggestionsActive : ""}`}
            >
                <div className={`${styles.qaExpandOverlayContent} ${!shouldShowRightSidePanel ? styles.qaExpandOverlayContentFull : ""}`}>
                    {/* Full UserChatMessage — edit, copy, version nav all work */}
                    {renderUserQuestion(expandedQAIndex)}

                    {/* Answer */}
                    <div className={styles.qaExpandOverlayAnswerArea} style={{ "--answer-container-width": "100%" } as React.CSSProperties}>
                        {renderUnifiedAnswer(answer, answer[0], expandedQAIndex, isThisAnswerStreaming, false)}
                    </div>
                </div>

                {/* Right panel — only rendered when active (sources / thought process / suggestions) */}
                {shouldShowRightSidePanel && (
                    <div className={styles.qaExpandOverlaySidePanelWrapper}>
                        {/* qaExpandOverlaySidePanelFill overrides the scroll-container to a plain flex
                            parent when suggestions is active — AllProductSuggestionsPanel uses height:100%
                            chains that only resolve correctly in a flex parent, NOT a scroll container */}
                        <div className={`${styles.qaExpandOverlaySidePanel} ${showSuggestionsPanel ? styles.qaExpandOverlaySidePanelFill : ""}`}>
                            {showSuggestionsPanel ? (
                                <AllProductSuggestionsPanel
                                    className={styles.chatSuggestionsPanelCard}
                                    allProducts={products}
                                    allProductAutoSelectConfig={allProductAutoSelectConfig}
                                    isAdminUser={isAdmin}
                                    allowContinueWithAll={allowContinueWithAll}
                                    onSelectProduct={productScenarioRegenerateContext ? handleProductScenarioRegenerateSelectProduct : undefined}
                                    onContinueWithAll={productScenarioRegenerateContext ? handleProductScenarioRegenerateContinueWithAll : undefined}
                                    onCancel={productScenarioRegenerateContext ? handleProductScenarioRegenerateCancel : undefined}
                                />
                            ) : (
                                <div className={styles.qaExpandOverlayAnalysisPanelFrame}>
                                    <AnalysisPanel
                                        className={styles.qaExpandOverlayAnalysisPanel}
                                        activeCitation={activeCitation}
                                        onActiveTabChanged={x => onToggleTab(x, expandedQAIndex)}
                                        citationHeight="calc(100vh - 190px)"
                                        answer={selectedAnalysisAnswer}
                                        activeTab={activeAnalysisPanelTab}
                                        index={selectedCitation}
                                        isAdminUser={isAdmin}
                                        onActiveContentChanged={citationIndex => setSelectedCitation(citationIndex)}
                                        isStreaming={isStreaming}
                                        sourcePage={"Chat"}
                                        isThoughtsLoading={
                                            activeAnalysisPanelTab === AnalysisPanelTabs.ThoughtProcessTab && thoughtsLoadingIndex === selectedAnswer
                                        }
                                        appType={selectedAnalysisAppType ?? selectedAppType}
                                        fillAvailableHeight
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QAExpandOverlay;
